import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { PageHeader, DataTable, StatusBadge, Sel, BranchSel, opts } from "@/components/kit";
import { ReceiptDialog } from "@/components/Payment";
import { api, useApi, fmtErr, dt, peso, label, METHODS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Payments() {
  const { branch, setBranch, branchName, can } = useAuth();
  const [f, setF] = useState({ q: "", status: "", method: "" });
  const [rcpt, setRcpt] = useState(null);
  const { data, loading, reload } = useApi("/payments", { ...f, branch_id: branch });
  const reverse = async (p, kind) => {
    const reason = window.prompt(`${kind === "refund" ? "Refund" : "Void"} reason for ${p.receipt_no}`);
    if (!reason) return;
    try {
      await api.post(`/payments/${p.id}/${kind}`, { reason });
      toast.success(`Payment ${kind === "refund" ? "refunded" : "voided"}`);
      reload();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  const total = (data || []).filter((p) => p.status === "successful").reduce((a, p) => a + p.amount, 0);
  return (
    <div className="fade-up">
      <PageHeader title="Payments" subtitle={can("view_financial") ? `Successful total in view: ${peso(total)} • SIMULATED gateway (GCash, Maya, QR Ph, Cards)` : "Payment transactions"} testid="payments-header">
        <Input data-testid="payments-search" className="w-48" placeholder="Receipt / plate / txn" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} />
        <Sel value={f.status} onChange={(v) => setF({ ...f, status: v })} testid="payments-status-filter" allLabel="All statuses" options={opts(["successful", "pending", "failed", "refunded", "voided"])} />
        <Sel value={f.method} onChange={(v) => setF({ ...f, method: v })} testid="payments-method-filter" allLabel="All methods" options={METHODS} />
        <BranchSel value={branch} onChange={setBranch} testid="payments-branch" />
      </PageHeader>
      <DataTable testid="payments-table" loading={loading} rows={data} columns={[
        { key: "created_at", label: "Date/time", render: (r) => dt(r.created_at) },
        { key: "receipt_no", label: "Receipt", render: (r) => <span className="font-mono text-xs">{r.receipt_no || "—"}</span> },
        { key: "branch", label: "Branch", render: (r) => branchName(r.branch_id) },
        { key: "plate", label: "Plate", render: (r) => <span className="plate text-xs">{r.plate}</span> },
        { key: "session_no", label: "Session", render: (r) => <span className="font-mono text-xs">{r.session_no}</span> },
        { key: "method", label: "Method", render: (r) => <span className="flex gap-1">{label(r.method)}{r.cash_exception && <StatusBadge status="cash_exception" tone="amber" />}</span> },
        { key: "transaction_id", label: "Txn ID", render: (r) => <span className="font-mono text-xs">{r.transaction_id || "—"}</span> },
        { key: "amount", label: "Amount", render: (r) => <span className="font-mono font-semibold">{peso(r.amount)}</span> },
        { key: "status", label: "Status", render: (r) => <span className="flex gap-1"><StatusBadge status={r.status} />{r.offline && <StatusBadge status="offline" tone="amber" />}</span> },
        { key: "act", label: "", render: (r) => (
          <span className="flex gap-2 text-xs">
            {r.receipt_no && <button className="text-primary underline" data-testid={`payment-receipt-${r.id}`} onClick={async () => setRcpt((await api.get(`/payments/${r.id}/receipt`)).data)}>Receipt</button>}
            {r.status === "successful" && can("refund") && <button className="text-amber-500 underline" data-testid={`payment-refund-${r.id}`} onClick={() => reverse(r, "refund")}>Refund</button>}
            {r.status === "successful" && can("refund") && <button className="text-red-500 underline" data-testid={`payment-void-${r.id}`} onClick={() => reverse(r, "void")}>Void</button>}
          </span>
        ) },
      ]} />
      <ReceiptDialog data={rcpt} onOpenChange={() => setRcpt(null)} />
    </div>
  );
}
